import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Col, Container, Row } from 'react-bootstrap';
import { v5 as uuidv5 } from 'uuid';
import { MediFindMoments, SignUpAd } from '@medifind/ads';
import { SymptomCheckerAd } from '@medifind/ads';
import { WidgetContainer } from '@medifind/containers';
import { useBookmarkLoader, usePermalinkLoader, useResultRouteQueryResponse, useSearchPageBookmarkPermalink } from '@medifind/hooks';
import { getConditionKeyword, getDoctor, publicationSearchResults } from '@medifind/interface';
import { usePageChanger, usePageHooks, useRouter } from '@medifind/router';
import {
  FilterSideContainer,
  Grid,
  PublicationCardV3,
  PublicationFiltersGroup,
  PublicationResultFilterList,
} from '@medifind/search-results';
import { SEO } from '@medifind/seo';
import { LastUpdate } from '@medifind/shared-basic-components';
import { getNullList } from '@medifind/utils';
import {
  clearPublicationFilters,
  publicationFiltersDependencies,
  useFilterContext,
  usePublicationCriteriaFilter,
  withFilters,
} from '@medifind/zustand';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import { logEventGA4 } from '@medifind/zustand';
import styles from './treatment-advances.module.scss';

const perPage = 12;

/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/treatment-advances */
/* __ROUTE_PATH__=/drugs/:drugId/treatment-advances */
/* __ROUTE_PATH__=/doctors/:personSlug/:personId/treatment-advances */
const AdvancesPage = () => {
  usePageHooks();

  const filters = useFilterContext();
  const router = useRouter();
  const { codingName, drugName, codingType } = useRouteStore(selectRouteParams);
  const isPublication = router.location.pathname.includes('/publications');
  const { personId, drugId, projectId, codingSlug, personSlug } = router.query;
  const { page } = usePageChanger('page', [...filters.valueDependencies, personId, projectId, drugId]);
  usePermalinkLoader({ filterList: filters.filterList });
  useBookmarkLoader({ filterList: filters.filterList });

  const query = {
    size: perPage,
    page,
    recent: !isPublication,
    drugId: drugId,
    personId: personId,
    criteria: filters.values.publicationCriteria,
  };

  const { data, error, notFound, results, totalResults } = useResultRouteQueryResponse({
    queryKey: [
      'publicationSearchResults',
      ...filters.dependencies,
      personId,
      drugId,
      isPublication,
      page,
      projectId,
      codingSlug,
      personSlug,
    ],
    queryFn: ({ signal }) => {
      if (!personId && projectId) {
        query.projectId = projectId;
      }
      return publicationSearchResults(query, signal);
    },
    enabled: filters.checkFilterDependencies([codingSlug, personSlug]),

    onError: (error) => {
      if (error !== null) {
        logEventGA4(
          'exception',
          { type: 'treatment-advances', method: error.code, description: error.message, item_id: projectId || personId || drugId },
          uuidv5(`treatment_advances${projectId ? `_${projectId}` : ''}`, '00000000-0000-0000-0000-000000000000'),
        );
      }
    },
  });

  const { data: criteriaKeywordsManual } = useQuery({
    queryKey: ['getConditionKeyword', projectId],
    queryFn: async () => {
      try {
        return await getConditionKeyword(projectId);
      } catch (e) {
        return null;
      }
    },
    select: ({ ctKeywords }) => {
      const keywords = ctKeywords?.map((d) => d.trim()).filter(Boolean);
      return keywords;
    },
    enabled: !!projectId,
  });

  const { data: doctor } = useQuery({
    queryKey: ['getDoctor', personId],
    queryFn: async () => {
      try {
        return await getDoctor(personId);
      } catch (e) {
        return null;
      }
    },
    enabled: !!personId,
  });

  useEffect(() => {
    filters.setFilterDependencies([codingSlug, personSlug]);
  }, [codingSlug, personSlug]);

  const type = isPublication ? 'Publications' : 'Latest Advances';
  let title = `${codingName} ${type}`;
  let description = `Looking to stay up to date with the latest research for ${codingName}? MediFind collects the most relevant articles that will help you make informed choices.`;
  if (doctor) {
    title = `${doctor.name} - ${type}`;
    description = `Find the latest advances by ${doctor.name}`;
  }
  if (drugId) {
    title = `${drugName} - Latest Advances`;
    description = `Find the latest advances by ${drugName}.`;
  }

  useSearchPageBookmarkPermalink(
    title,
    'latest-advance-search',
    {
      drugId,
      personId,
      projectId,
    },
    {
      personSlug,
      doctorName: doctor?.name,
      drugName: drugName,
    },
    filters.filterList,
    [...filters.dependencies, drugId, personId, doctor?.name, drugName],
  );

  if (notFound) {
    throw new Error(404);
  }
  return (
    <>
      <SEO noIndex={notFound} title={`${title} | MediFind`} description={description} />

      <Container>
        <Row>
          {/* left-side filters/match to trials */}
          <Col sm={12} lg={4} xl={3}>
            <div className="hide--md">
              <FilterSideContainer
                title="Customize your search results with filters"
                lazyLoadOptions={{ height: 600, resize: true }}
                clearFilters={clearPublicationFilters}
              >
                <PublicationResultFilterList criteriaKeywords={criteriaKeywordsManual || data?.keywords} totalResults={totalResults} />
              </FilterSideContainer>

              <MediFindMoments
                type={'rectangle'}
                targeting={{
                  position: 'mid1',
                  pageType: 'publication-search',
                }}
              />

              {results && <LastUpdate postion={'top'} resultsLoaded={!!results} dateModify={results[0]?.dateModify} />}
              <div>
                <SignUpAd tagLine="Save publications for later" loginMessage="You can save publications with a MediFind account." />
                <SymptomCheckerAd />
              </div>
            </div>
          </Col>
          {/* results list */}
          <Col sm={12} lg={8} xl={9} className={styles['outer__col']}>
            <>
              <MediFindMoments
                type={'leaderboard'}
                targeting={{
                  position: 'top',
                  pageType: 'publication-search',
                }}
              />
            </>
            <WidgetContainer>
              <PublicationFiltersGroup
                criteriaKeywords={criteriaKeywordsManual || data?.keywords}
                totalResults={totalResults}
                smallAt="md"
              />

              <Grid
                error={error}
                items={(results || getNullList(perPage)).map((publication, i) => (
                  <PublicationCardV3
                    key={i}
                    {...router.query}
                    publication={publication}
                    codingType={codingType}
                    isPublication={isPublication}
                    id={isPublication ? 'publications-' + i : 'treatment-latest-advances-' + i}
                    priority={3}
                    smallAt={['xs', 'lg']}
                  />
                ))}
                noBorder
                addGutters
                title={isPublication ? 'publications' : 'latest advances'}
                size={perPage}
                id={isPublication ? 'top-publications' : 'top-recent-advances'}
                maxColumnCount={1}
                pagination={{
                  page: page,
                  totalResults: totalResults,
                  type: 'link',
                  forceScroll: true,
                  noResults: isPublication ? '0 Publications Found' : '0 Latest Advances Found',
                  requireLogin: {
                    errorMessage: `Sign in or create a free MediFind account to see all the cutting-edge ${codingName} research. Filter and save these articles to stay on top of the latest developments.`,
                    errorTitle: `More Advances Await`,
                    afterPage: 1,
                  },
                }}
                insertResult={[
                  {
                    renderAfter: (i) => (i + 1) % 4 === 0,
                    render: (i) => (
                      <MediFindMoments
                        type={'rectangle'}
                        targeting={{
                          position: `mid${(i + 1) / 4 + 1}`,
                          pageType: 'publication-search',
                        }}
                      />
                    ),
                    dontShowAtEnd: false,
                  },
                ]}
              />
            </WidgetContainer>
            {results && <LastUpdate postion={'bottom'} resultsLoaded={!!results} dateModify={results[0]?.dateModify} />}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default withFilters([usePublicationCriteriaFilter], [publicationFiltersDependencies], [clearPublicationFilters])(AdvancesPage);
